import React, { useState, useRef } from 'react';
import { Cropper, FixedCropperRef } from 'react-advanced-cropper';
import styled from 'styled-components';
import { Dialog, ElevatedButton, IconButton, TextButton } from '@producer/ui-kit';

import { Tooltip } from '../tooltip/Tooltip';
import { Crop as CropIcon } from '../icons/crop';
import { RotateLeft, RotateRight } from '../icons';
import { PhotoUploader } from '../photo-uploader/photo-uploader';
import { imagePreview } from './utils/image-preview';

import 'react-advanced-cropper/dist/style.css';
import { breakPoint } from '../../app/theme';

type PhotoCropperModalProps = {
  onCancel: () => void;
  onSave: (file: File) => void;
  imgFile?: File;
  aspectRatio?:
    | number
    | {
        minimum: number;
        maximum: number;
      };
  theme?: 'light' | 'dark';
};

export const PhotoCropperModal: React.FC<PhotoCropperModalProps> = ({
  onCancel,
  onSave,
  aspectRatio = 16 / 9,
  imgFile,
  theme = 'dark',
}) => {
  const [imgSrc, setImgSrc] = useState(imgFile ? URL.createObjectURL(imgFile) : '');
  const cropperRef = useRef<FixedCropperRef>(null);
  const [isLoading, setIsLoading] = useState<boolean>(false);

  const originalFileRef = useRef<File | undefined>(imgFile);
  const originalFile = originalFileRef.current;

  const onSelectFile = (file: File) => {
    if (file) {
      originalFileRef.current = file;
      const reader = new FileReader();
      reader.addEventListener('load', () => setImgSrc(reader.result?.toString() || ''));
      reader.readAsDataURL(file);
    }
  };

  const rotate = (angle: number) => {
    if (cropperRef.current) {
      cropperRef.current.rotateImage(angle);
    }
  };

  const handleOnSave = async () => {
    if (isLoading) {
      return;
    }

    if (!cropperRef.current) {
      return;
    }

    setIsLoading(true);

    const { blob } = await imagePreview(cropperRef.current.getCanvas()!);

    const file = new File([blob!], originalFile!.name, { type: originalFile!.type });

    onSave(file);
    setIsLoading(false);
    onCancel();
  };

  return (
    <Dialog
      title="Edit Image"
      onClose={onCancel}
      actions={[
        <TextButton key="cancel" text="Cancel" onClick={onCancel} />,
        <ElevatedButton text={'Save'} key={'save'} onClick={handleOnSave} isLoading={isLoading} />,
      ]}
    >
      <Container>
        {!imgSrc ? (
          <UploaderContainer>
            <PhotoUploader onChange={onSelectFile} />
          </UploaderContainer>
        ) : (
          <Cropper
            ref={cropperRef}
            src={imgSrc}
            {...(theme === 'light' && { boundaryClassName: 'cropper-light-theme--boundary' })}
            stencilProps={{
              handlers: true,
              lines: true,
              movable: true,
              resizable: true,
              aspectRatio,
              ...(theme === 'light' && {
                overlayClassName: 'cropper-light-theme--overlay',
                handlerClassNames: {
                  default: 'cropper-light-theme--handler',
                },
                lineClassNames: {
                  default: 'cropper-light-theme--stencil-line',
                },
              }),
            }}
          />
        )}
      </Container>

      <Options>
        <OptionsWrapper>
          <Tooltip text={'Crop'} position="top" disabled={!imgSrc}>
            <IconButton
              variant="transparent"
              onClick={() => {}}
              icon={<CropIcon />}
              disabled={!imgSrc}
            />
          </Tooltip>

          <Tooltip text={'Turn Left'} position="top" disabled={!imgSrc}>
            <IconButton
              variant="transparent"
              onClick={() => {
                rotate(-90);
              }}
              icon={<RotateLeft />}
              disabled={!imgSrc}
            />
          </Tooltip>

          <Tooltip text={'Turn Right'} position="top" disabled={!imgSrc}>
            <IconButton
              variant="transparent"
              onClick={() => {
                rotate(90);
              }}
              icon={<RotateRight />}
              disabled={!imgSrc}
            />
          </Tooltip>
        </OptionsWrapper>
      </Options>
    </Dialog>
  );
};

const Container = styled.div`
  min-width: 0;
  width: 90vw;
  max-width: 72rem;
  aspect-ratio: 16 / 9;
  display: flex;
  align-items: center;
  justify-content: center;

  @media screen and (min-width: ${breakPoint.small}px) {
  }
`;

const UploaderContainer = styled.div`
  width: 100%;
  height: 100%;
`;

const Options = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  margin-top: 1.6rem;
`;

const OptionsWrapper = styled.div`
  display: flex;
  flex-direction: row;
  align-items: center;
  justify-content: space-between;
  gap: 1.6rem;
`;
